import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BContainer } from "@/lib/typed-bootstrap";

import BillingDataForm from "@/components/org-management/BillingDataForm.component";

import { getCheckoutSessionForApi } from "@/models/checkout-session";
import { OrgModule } from "@/store/modules/org";

import {
  ICheckoutSession,
  ICheckoutSessionUpdateInput
} from "@/generated/types";
import BillingAccount from "@/mixins/BillingAccount.mixin";
import CheckoutSession from "@/mixins/CheckoutSession.mixin";
import ErrorHandling from "@/mixins/ErrorHandling.mixin";

import {
  Button,
  ButtonShapeEnum,
  ButtonSizeEnum,
  ButtonThemeEnum
} from "@/components/shared/Button.component";
import { Loader } from "@/components/shared/Loader.component";
import styles from "./ProjectBilling.view.module.scss"; // Assume the styles were imported

export const mapCheckoutSessionToInput = (
  state: ICheckoutSession,
  paymentProviderId?: string
): ICheckoutSessionUpdateInput => ({
  ...state,
  orgId: OrgModule.id,
  apiId: state.apiId ?? "",
  paymentProviderId: paymentProviderId ?? "",
  sessionId: state.sessionId ?? "",
  bundleType: state.bundleType ?? "",
  cancelUrl: state.cancelUrl ?? "",
  successUrl: state.successUrl ?? "",
  description: state.description ?? "",
  lineItems: state.lineItems ?? "",
  products: state.products ?? []
});

@Component
export default class ProjectBilling extends mixins(
  BillingAccount,
  CheckoutSession,
  ErrorHandling
) {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  isLoading = false;
  form: any = null;

  get apiId() {
    return this.$route.params.apiId;
  }

  get billingAccount() {
    return OrgModule.synth().billingAccount;
  }

  get hasValidBillingAccount() {
    return /^cus_\w+/.test(this.billingAccount?.paymentProviderId ?? "");
  }

  async onContinue(): Promise<void> {
    try {
      this.isLoading = true;
      this.errorMessage = "";

      if (!this.billingAccount?.paymentProviderId) {
        throw new Error(this.$t("error_billing_account_creation").toString());
      }

      const session = await getCheckoutSessionForApi(this.apiId);

      if (session) {
        const updatedSession = await this.upsertCheckoutSession(
          mapCheckoutSessionToInput(
            session,
            this.billingAccount?.paymentProviderId
          )
        );

        if (updatedSession?.sessionId && updatedSession?.sessionUrl) {
          if (window.Cypress) {
            this.$router.push({
              name: "project-setup-publishing",
              params: { apiId: this.apiId },
              query: { session_id: session?.sessionId }
            });
          } else {
            window.location.href = updatedSession.sessionUrl;
          }
        }
      } else {
        this.$router.push({
          name: "api-setup-bundles",
          params: { apiId: this.apiId }
        });
      }
    } catch (err) {
      this.errorMessage = (err as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return (
      <BContainer class={styles.view}>
        <h1>{this.$t("api_setup_billing_headline")}</h1>
        <p>{this.$t("api_setup_billing_subline")}</p>

        <BContainer class="mt-5">
          <BAlert
            show={this.showErrorMessage}
            variant="danger"
            data-testid="error-alert"
          >
            {this.errorMessage}
          </BAlert>

          <BillingDataForm
            onBillingAccountUpdated={this.onContinue}
            scopedSlots={{
              "form-buttons": (formProps) =>
                formProps.showButtons ? (
                  <div class={styles.buttons}>
                    <Button
                      type="submit"
                      class={styles.actionButton}
                      shape={ButtonShapeEnum.square}
                      theme={ButtonThemeEnum.primary}
                      size={ButtonSizeEnum.lg}
                      data-testid="continue-button"
                      onClick={(e) => {
                        e.preventDefault();
                        formProps.onSave(e);
                      }}
                      disabled={formProps.isLoading || this.isLoading}
                    >
                      {this.isLoading ? (
                        <Loader />
                      ) : (
                        this.$t("form_cta_continue")
                      )}
                    </Button>
                  </div>
                ) : null
            }}
          ></BillingDataForm>
        </BContainer>
      </BContainer>
    );
  }
}
